import { useEffect, useMemo, useState } from 'react';
import {
  loadOSForReport,
  filterOSByDateRange,
  calculateReportTotals,
  calculateReportCounts,
  calculateTicketMedio,
  calculatePercentualReceita,
  bucketOSByMonth,
} from '../utils/osReport';
import { loadEntriesSummary, loadInsumos, loadMonthlyDespesas } from '../utils/financialDashboard';
import { loadBudgetValueMetrics } from '../utils/budgetMetrics';

const EMPTY_INSUMOS = { funilaria: 0, estetica: 0 };
const EMPTY_BUDGET = {
  realizados: 0, aprovados: 0, naoAprovados: 0, taxaConversao: 0, ticketMedio: 0,
  qtdRealizados: 0, qtdAprovados: 0, qtdNaoAprovados: 0,
};

// Agrega as fontes do dashboard geral. OS carregam por empresa (filtro de
// período é client-side, igual ao relatório); financeiro/orçamentos são
// ranged no servidor. Cada fonte tem try/catch — uma falha não derruba o resto.
export const useGeneralDashboardData = ({ companyId, periodRange, refreshKey }) => {
  const [osRows, setOsRows] = useState([]);
  const [osLoading, setOsLoading] = useState(true);

  const [despesas, setDespesas] = useState(0);
  const [insumos, setInsumos] = useState(EMPTY_INSUMOS);
  const [budget, setBudget] = useState(EMPTY_BUDGET);
  const [monthlyDespesas, setMonthlyDespesas] = useState([]);
  const [finLoading, setFinLoading] = useState(true);

  const from = periodRange?.from;
  const to = periodRange?.to;

  useEffect(() => {
    if (!companyId) {
      setOsRows([]);
      setOsLoading(false);
      return;
    }
    let cancelled = false;
    setOsLoading(true);
    loadOSForReport(companyId)
      .then((rows) => {
        if (!cancelled) setOsRows(rows);
      })
      .catch(() => {
        if (!cancelled) setOsRows([]);
      })
      .finally(() => {
        if (!cancelled) setOsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  useEffect(() => {
    if (!companyId || !from || !to) {
      setDespesas(0);
      setInsumos(EMPTY_INSUMOS);
      setBudget(EMPTY_BUDGET);
      setMonthlyDespesas([]);
      setFinLoading(false);
      return;
    }
    let cancelled = false;
    setFinLoading(true);
    const range = { from, to };
    const refDate = new Date(`${to}T00:00:00`);
    Promise.allSettled([
      loadEntriesSummary(companyId, range),
      loadInsumos(companyId, range),
      loadBudgetValueMetrics(companyId, range),
      loadMonthlyDespesas(companyId, refDate, 6),
    ]).then(([summaryR, insumosR, budgetR, monthlyR]) => {
      if (cancelled) return;
      setDespesas(summaryR.status === 'fulfilled' ? summaryR.value.despesas : 0);
      setInsumos(insumosR.status === 'fulfilled' ? insumosR.value : EMPTY_INSUMOS);
      setBudget(budgetR.status === 'fulfilled' ? budgetR.value : EMPTY_BUDGET);
      setMonthlyDespesas(monthlyR.status === 'fulfilled' ? monthlyR.value : []);
      setFinLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [companyId, from, to, refreshKey]);

  const osMetrics = useMemo(() => {
    const filtered = filterOSByDateRange(osRows, from, to);
    const totals = calculateReportTotals(filtered);
    const counts = calculateReportCounts(filtered);
    return {
      totals,
      counts,
      ticketMedio: calculateTicketMedio(totals, counts),
      percentualReceita: calculatePercentualReceita(totals),
    };
  }, [osRows, from, to]);

  const monthlySeries = useMemo(() => {
    const refDate = to ? new Date(`${to}T00:00:00`) : new Date();
    const fat = bucketOSByMonth(osRows, refDate, 6);
    return fat.map((f, i) => {
      const despesasMes = monthlyDespesas[i]?.despesas || 0;
      return {
        mes: f.mes,
        faturamento: f.faturamento,
        despesas: despesasMes,
        resultado: f.faturamento - despesasMes,
        os: f.os,
        ticket: f.os > 0 ? f.faturamento / f.os : 0,
      };
    });
  }, [osRows, to, monthlyDespesas]);

  return {
    loading: osLoading || finLoading,
    totals: osMetrics.totals,
    counts: osMetrics.counts,
    ticketMedio: osMetrics.ticketMedio,
    percentualReceita: osMetrics.percentualReceita,
    despesas,
    insumos,
    budget,
    monthlySeries,
  };
};
